/* eslint-disable react/jsx-props-no-spreading */
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ApiProduct } from '../../../../../models/ecommerce/product/ApiProduct';
import {
    createProductPageContext,
    trackSpaDY,
    updateDyStaticContext,
} from '../../../../../utility/dynamicYield';
import DynamicYieldBlock from '../../../../common/components/DynamicYieldBlock/DynamicYieldBlock';
import PageProperties from '../../../../common/components/molecules/PageProperties';
import SamePromotionProductList from '../../../../common/components/organisms/SamePromotionProductList';
import { productFlow } from '../../../../common/flow/product/productFlow';
import trackingFlow from '../../../../common/flow/trackingFlow';
import { useAsyncDispatcher } from '../../../../common/hooks/useAsyncDispatcher';
import { useBuyableState } from '../../../../common/hooks/useBuyableProductState';
import { useCurrentPriceType } from '../../../../common/hooks/useCurrentPriceType';
import { useGroupError } from '../../../../common/hooks/useGroupError';
import { useLayoutSize } from '../../../../common/hooks/useLayoutSize';
import { useAppSelector } from '../../../../common/hooks/useThunkDispatch';
import { storeSelectors } from '../../../../common/selectors/storeSelectors';
import { ErrorGroup } from '../../../../common/store/structureDefinitions/errorsState';
import { ga4ItemsTracking } from '../../../../common/tracking/ga4/items';
import { useIsB2BRoute } from '../../../../common/utils/b2bUtils';
import EpiContentArea from '../../../../episerver/components/base/EpiContentArea';
import { mapEpiDynamicYieldBlockToProps } from '../../../../episerver/components/blocks/EpiDynamicYieldBlock/EpiDynamicYieldBlock';
import { isDynamicYieldBlock } from '../../../../episerver/components/blocks/EpiDynamicYieldBlock/EpiDynamicYieldBlock.utils';
import { useCurrentProductOrSection } from '../../../hooks/useCurrentProductOrSection';
import useEcommerceSettings from '../../../hooks/useEcommerceSettings';
import ProductJsonLd from '../../JsonLd/ProductJsonLd';
import SustainabilityDeclarationButton from '../../SustainabilityDeclarationButton/SustainabilityDeclarationButton';
import NotFoundPage from '../notfoundPage/NotFoundPage';
import ProductDetails from './ProductDetails/ProductDetails';
import ProductDetailsAddToCartButton from './ProductDetails/ProductDetailsAddToCartButton';
import ProductDetailsVariantSelector from './ProductDetails/ProductDetailsVariantSelector';
import ProductDetailsBreadcrumbs from './ProductDetailsBreadcrumbs';
import { getProductDetailsPageMetaData, mapToProductDetailsData } from './ProductDetailsPage.utils';
import ProductDetailsPrices from './ProductDetailsPrices';
import ProductImageTrigger from './ProductImage/ProductImageTrigger';
import RecipeRecommendationsByProduct from './RecipeRecommendationsByProduct/RecipeRecommendationsByProduct';

/** Also known as PDP */
const ProductDetailsPage: FC = () => {
    const {
        routeData: { productId },
    } = useCurrentProductOrSection();

    const {
        execute,
        result: product,
        error,
        status,
    } = useAsyncDispatcher(productFlow.getProductById);

    const currentProductionUnit = useAppSelector(storeSelectors.currentProductionUnit);

    useEffect(() => {
        if (productId) {
            execute(productId, currentProductionUnit);
        }
    }, [execute, productId, currentProductionUnit]);

    const { setContentPageSize } = useLayoutSize();

    useEffect(() => {
        setContentPageSize('full');
    }, [setContentPageSize]);

    const [dyContextReady, setDyContextReady] = useState(false);

    useEffect(() => {
        if (productId) {
            const context = createProductPageContext(window.location.href, productId);
            trackSpaDY(context, window.location.href);
            trackingFlow.click({ productId }); // this will be sent to loop54, according to them, click can be tracked on pdp page.
            updateDyStaticContext(context);
            setDyContextReady(true);
        }
    }, [productId]);

    useGroupError(ErrorGroup.Global, error);

    if (!productId) {
        return null;
    }
    if (status === 'success' && !product) {
        return <NotFoundPage type="product" />;
    }
    return (
        // this Fragment is necessary for Portal to work properly
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>{product && dyContextReady && <ProductDetailsWrapper product={product} />}</>
    );
};

const ProductDetailsWrapper = (props: { product: ApiProduct }) => {
    const currentProductionUnit = useAppSelector(storeSelectors.currentProductionUnit);

    const { currentPriceType } = useCurrentPriceType();

    const { currentVariant, quantity, handleVariantChange, onQuantityUpdate } = useBuyableState(
        props.product,
        {
            isSilent: true,
            analyticsOptions: {
                listId: 'product_details_page',
                list: 'Product Details Page',
                position: 0,
            },
        },
    );

    const product = useMemo(
        () => mapToProductDetailsData(props.product, currentVariant || undefined),
        [props.product, currentVariant],
    );

    useEffect(() => {
        if (props.product) {
            ga4ItemsTracking.viewItem(
                props.product.identifier,
                props.product.variants?.[0]?.id,
                currentProductionUnit,
            );
        }
    }, [currentProductionUnit, props.product]);

    const title =
        product.manufacturer && product.name.length + product.manufacturer.length < 45
            ? `${product.name} - ${product.manufacturer} - Coop`
            : `${product.name} - Coop`;

    const description = product.manufacturer
        ? `Köp ${product.name} från ${product.manufacturer} online på Coop.se. Välj mellan hemleverans eller upphämtning i butik. Handla här!`
        : `Köp ${product.name} online på Coop.se. Välj mellan hemleverans eller upphämtning i butik. Handla här!`;

    const isB2BRoute = useIsB2BRoute();
    const { ogImage, canonicalUrl } = getProductDetailsPageMetaData(product, isB2BRoute);

    const hasVariants = !!product.variants?.length;
    const { data: ecommerceSettings } = useEcommerceSettings();

    const [showSimiliarityBlock, setShowSimiliarityBlock] = useState(
        !product?.promotion?.identifier,
    );
    const onPromoProductsLoaded = useCallback((promoProductsCount: number) => {
        if (promoProductsCount === 0) {
            setShowSimiliarityBlock(true);
        }
    }, []);

    return (
        <>
            <PageProperties
                headData={{
                    title,
                    link: {
                        canonical: canonicalUrl,
                    },
                    meta: {
                        description,
                        'og:image': ogImage,
                    },
                }}
                pageTypeNameForAnalytics="produktsida"
            />
            <ProductJsonLd {...props.product} priceType={currentPriceType} />
            <ProductDetailsBreadcrumbs name={product.name} productUrl={product.url} />
            <div className="Grid">
                <div className="Grid-cell u-marginBxlg">
                    <ProductDetails
                        product={product}
                        priceType={currentPriceType}
                        productImageSlot={
                            <ProductImageTrigger name={product.name} imageUrl={product.imageUrl} />
                        }
                        aboveAddToCartSlot={
                            <>
                                {hasVariants && (
                                    <ProductDetailsVariantSelector
                                        productName={product.name}
                                        selectedVariantId={currentVariant?.id}
                                        variants={product.variants}
                                        handleVariantChange={handleVariantChange}
                                    />
                                )}
                                <ProductDetailsPrices
                                    product={product}
                                    variant={currentVariant || undefined}
                                    priceType={currentPriceType}
                                />
                            </>
                        }
                        addToCartSlot={
                            <ProductDetailsAddToCartButton
                                quantity={quantity}
                                size={48}
                                withButton
                                setQuantity={onQuantityUpdate}
                                productName={product.name}
                                buyButtonText="Lägg i varukorg"
                                getBuyButtonLabel={(name) => `Lägg i varukorg, ${name}`}
                                theme="secondary"
                            />
                        }
                        sustainabilityButtonSlot={
                            <SustainabilityDeclarationButton product={product} />
                        }
                    />
                </div>
                <div className="Grid-cell">
                    <div className="Grid Grid--gutterVmd">
                        <div className="Grid-cell">
                            {showSimiliarityBlock && ecommerceSettings ? (
                                <DynamicYieldBlock
                                    key={props.product.identifier} // force refetch recommendations when navigating to different product
                                    as="aside"
                                    recommendationId={
                                        ecommerceSettings.similiarityBlockRecommendationId
                                    }
                                    onlyPromotions={false}
                                    analyticsListName={
                                        ecommerceSettings.similiarityBlockRecommendationGaList
                                    }
                                    useSwiper
                                    lazyLoad
                                    horizontalHidden={false}
                                    disableAsyncLink={false}
                                />
                            ) : (
                                <SamePromotionProductList
                                    product={product}
                                    onProductsLoaded={onPromoProductsLoaded}
                                />
                            )}
                        </div>

                        {ecommerceSettings && (
                            <EpiContentArea
                                items={ecommerceSettings.bottomContentArea}
                                renderItem={(
                                    componentProps,
                                    _contentAreaItem,
                                    contentAreaItemClasses,
                                ) => {
                                    if (isDynamicYieldBlock(componentProps)) {
                                        const mappedProps =
                                            mapEpiDynamicYieldBlockToProps(componentProps);

                                        return (
                                            <div className={contentAreaItemClasses}>
                                                <DynamicYieldBlock as="aside" {...mappedProps} />
                                            </div>
                                        );
                                    }
                                }}
                            />
                        )}
                        <div className="Grid-cell">
                            <RecipeRecommendationsByProduct
                                productIdentifier={product.identifier}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ProductDetailsPage;
